import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RxIconModule } from '@nrwl/rx-icon';
import { SoapNotesService, SoapPlanItem } from '../../soap-notes.service';

type SoapPlanItemsDialogButtons = Record<string, { text?: string }>;

interface SoapPlanItemsDialogPayload {
  title?: string;
  planItems?: SoapPlanItem[];
  planItemsQuantity?: string[] | number[] | string | number | null;
  buttons?: SoapPlanItemsDialogButtons;
}

interface SoapPlanItemsDialogResult {
  items: SoapPlanItem[];
  quantities: string[];
}

type SoapPlanItemsDialogValue = SoapPlanItemsDialogPayload | SoapPlanItemsDialogResult;

@Component({
  selector: 'fb-soap-plan-items-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, RxIconModule],
  template: `
    <div class="dialog">
      <div class="dialog__search">
        <rx-icon name="icon-search" [size]="12"></rx-icon>
        <input
          type="text"
          placeholder="Search Plan Items"
          [(ngModel)]="searchTerm"
          (input)="applyFilter()"
        />
      </div>

      <div class="dialog__table">
        <div class="dialog__loader" *ngIf="isLoading">
          <div class="dialog__spinner"></div>
          <div class="dialog__loader-text">Loading plan items...</div>
        </div>
        <ng-container *ngIf="!isLoading">
          <div class="dialog__row dialog__row--header">
            <div>Item Name</div>
            <div class="dialog__amount">Amount</div>
          </div>
          <div class="dialog__row" *ngFor="let item of filteredItems">
            <div class="dialog__item-name">{{ item.name }}</div>
            <input
              class="dialog__amount-input"
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              [value]="getQuantity(item)"
              (keydown)="onQuantityKeydown($event)"
              (input)="onQuantityInput($event, item)"
              placeholder="Amount"
            />
          </div>
          <div class="dialog__empty" *ngIf="filteredItems.length === 0">No plan items</div>
        </ng-container>
      </div>
    </div>
  `,
  styles: [
    `
      .dialog {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }

      .dialog__search {
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #e5e7eb;
        border-radius: 999px;
        padding: 8px 12px;
      }

      .dialog__search input {
        border: none;
        outline: none;
        width: 100%;
        font-size: 13px;
      }

      .dialog__table {
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        overflow: auto;
        max-height: 340px;
        min-height: 200px;
        position: relative;
      }

      .dialog__row {
        display: grid;
        grid-template-columns: 1fr 120px;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border-bottom: 1px solid #e5e7eb;
      }

      .dialog__row--header {
        background: #f9fafb;
        font-size: 12px;
        font-weight: 600;
        color: #4b5563;
      }

      .dialog__row:last-child {
        border-bottom: none;
      }

      .dialog__amount {
        text-align: right;
      }

      .dialog__amount-input {
        width: 100%;
        border: 1px solid #e5e7eb;
        border-radius: 6px;
        padding: 6px 8px;
        font-size: 12px;
        text-align: right;
      }

      .dialog__empty {
        padding: 12px;
        font-size: 13px;
        color: #6b7280;
      }

      .dialog__loader {
        height: 200px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #6b7280;
        font-size: 13px;
      }

      .dialog__spinner {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: 2px solid #e5e7eb;
        border-top-color: #2563eb;
        animation: spin 0.8s linear infinite;
      }

      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class SoapPlanItemsDialogComponent implements OnInit {
  payload: SoapPlanItemsDialogValue = {};
  items: SoapPlanItem[] = [];
  filteredItems: SoapPlanItem[] = [];
  searchTerm = '';
  isLoading = true;
  private quantities: Record<string, string> = {};

  constructor(private soapNotesService: SoapNotesService) {}

  ngOnInit(): void {
    const payload = this.isPayloadConfig(this.payload) ? this.payload : undefined;
    const planItems = Array.isArray(payload?.planItems) ? payload.planItems : [];
    this.seedQuantities(planItems, payload?.planItemsQuantity);
    this.soapNotesService.getPlanItemsCatalog().subscribe((items) => {
      this.items = items;
      this.applyFilter();
      this.payload = this.buildPayload();
      this.isLoading = false;
    });
  }

  applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredItems = [...this.items];
      return;
    }

    this.filteredItems = this.items.filter((item) => item.name.toLowerCase().includes(term));
  }

  getQuantity(item: SoapPlanItem): string {
    return this.quantities[this.itemKey(item)] || '';
  }

  onQuantityInput(event: Event, item: SoapPlanItem): void {
    const target = event.target as HTMLInputElement;
    const sanitized = target.value.replace(/[^0-9]/g, '');
    this.quantities[this.itemKey(item)] = sanitized;
    if (target.value !== sanitized) {
      target.value = sanitized;
    }

    this.payload = this.buildPayload();
  }

  onQuantityKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (allowed.includes(event.key)) {
      return;
    }

    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  private seedQuantities(planItems: SoapPlanItem[], planItemsQuantity: unknown): void {
    const items = Array.isArray(planItems) ? planItems : [];
    const quantities = Array.isArray(planItemsQuantity) ? planItemsQuantity : [];

    items.forEach((item, index) => {
      const key = this.itemKey(item);
      const rawQuantity = quantities[index];
      if (key && rawQuantity !== undefined && rawQuantity !== null && rawQuantity !== '') {
        this.quantities[key] = String(rawQuantity);
      }
    });
  }

  private buildPayload(): SoapPlanItemsDialogResult {
    const selected = this.items
      .map((item) => ({
        item,
        quantity: this.quantities[this.itemKey(item)] || '',
      }))
      .filter((entry) => entry.quantity !== '');

    return {
      items: selected.map((entry) => entry.item),
      quantities: selected.map((entry) => entry.quantity),
    };
  }

  private itemKey(item: SoapPlanItem): string {
    return item.id || item.name;
  }

  private isPayloadConfig(payload: SoapPlanItemsDialogValue): payload is SoapPlanItemsDialogPayload {
    return !Array.isArray((payload as SoapPlanItemsDialogResult).items);
  }
}
